/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import { useValidImage } from '@hooks/useValidImage';
import {
	Box,
	Flex,
	NumberFormatter,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { getCurrencyUnit } from '@utils/currency-units';
import { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button/Button';
import { Text } from '../Text/Text';
import { Title } from '../Title/Title';
import classes from './AdditionalityPoolProjectCard.module.css';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

export type TProjectCardProps = {
	title: string;
	cover: string;
	description: string;
	financing: {
		unlimited: boolean;
		pledged_euro: number;
		target_euro?: number;
	};
	onLearnMore: MouseEventHandler<HTMLDivElement>;
	fullWidth: boolean;
	showMonetization: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const AdditionalityPoolProjectCard: React.FC<
	TProjectCardProps
> = ({
	title,
	description,
	fullWidth,
	cover,
	financing,
	onLearnMore,
	showMonetization = true,
}) => {
	const { t } = useTranslation();

	const { ref: refCover, width: widthCover } =
		useElementSize();

	const coverUrl = useValidImage(cover);

	/* ******************************** RENDERING ******************************* */
	return (
		<Box
			ref={refCover}
			onClick={onLearnMore}
			maw={fullWidth ? undefined : '380px'}
			p={'var(--mantine-spacing-md)'}
			className={classes.container}
			style={{
				width: '100%',
				borderRadius: '24px',
				position: 'relative',
				background: `radial-gradient(
                        circle at 0% 0%, 
                        rgba(255, 255, 255, 0.7) 0%, 
                        rgba(255, 255, 255, 0.7) 30%, 
                        rgba(255, 255, 255, 0) 75%
                    ),
		lightgray url(${coverUrl}) right bottom / cover no-repeat
	`,
			}}
		>
			<Flex
				direction={'column'}
				justify={'flex-start'}
				align={'flex-start'}
			>
				<Title
					maw={'390px'}
					order={4}
					c={'var(--mantine-color-igluPrimary-9)'}
				>
					{title}
				</Title>
				<Flex
					mt={'var(--mantine-spacing-sm)'}
					maw={widthCover < 400 ? '300px' : '55%'}
				>
					<Text
						span
						variant={'body1'}
						c={'var(--mantine-color-igluTextPrimary-9)'}
					>
						{description}
					</Text>
				</Flex>
				<Button
					mt={'var(--mantine-spacing-md)'}
					mb={'var(--mantine-spacing-lg)'}
					variant={'primary'}
				>
					{t('global.learnMore')}
				</Button>
				{showMonetization && (
					<Text
						variant={'subtitle1'}
						c={'var(--mantine-color-igluPrimary-9)'}
					>
						{t('global.financialNeeds')}
						{' : '}
						<Text
							span
							variant="subtitle1"
							c="var(--mantine-color-igluPrimary-9)"
						>
							{financing.unlimited ? (
								t('global.unlimited')
							) : (
								<NumberFormatter
									thousandSeparator=" "
									value={financing?.target_euro ?? 0}
									suffix={' ' + getCurrencyUnit('EUR')}
									decimalScale={2}
								/>
							)}
						</Text>
					</Text>
				)}
				{showMonetization && (
					<Text
						variant={'subtitle1'}
						c={'var(--mantine-color-igluPrimary-9)'}
					>
						{t('global.fundedPart')}
						{' : '}
						<Text
							span
							variant={'subtitle1'}
							c={'var(--mantine-color-igluPrimary-9)'}
						>
							<NumberFormatter
								thousandSeparator=" "
								value={financing.pledged_euro}
								suffix={' ' + getCurrencyUnit('EUR')}
								decimalScale={2}
							/>
						</Text>
					</Text>
				)}
			</Flex>
		</Box>
	);
};
